import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { OrganizationUnit, useMyUnits } from '~/data/orgunits';
import { StoreState } from '~/reducer';
import {
  orgUnitsStoreStateSelector,
  surveyStoreStateSelector,
} from '~/selectors';
import { AppDispatch } from '~/store';

import {
  closeAnswerAndResultsDialog,
  fetchFilteredSurveyResults,
  fetchSurveyAnswers,
  fetchSurveyResults,
} from '../slice';
import { ChildInput, SurveyResultsObject, UnitType } from '../types';

import { exportSurveyResultsToExcel } from './ExportSurveyResults';

export type UseSurveyResultsDialogOutput = {
  open: boolean;
  onDialogClose: () => void;
  print: () => void;
  surveyResults: SurveyResultsObject;
  menuList: ChildInput[];
  selectValue: number;
  selectOnChange: (value: number) => void;
  isLoading: boolean;
  failedToLoad: boolean;
  exportResults: () => void;
};

export const useSurveyResults = (
  resultId: string,
  handleResultsClose: () => void,
): UseSurveyResultsDialogOutput => {
  const {
    surveyReviewers,
    surveyAudience,
    surveyQuestions,
    surveyRawResults,
    surveyResults,
  } = useSelector((state: StoreState) => {
    return surveyStoreStateSelector(state);
  });
  const { isLoading, failedToLoad } = useSelector((state: StoreState) => {
    return orgUnitsStoreStateSelector(state);
  });
  const [selectValue, setSelectValue] = useState<number>(-1);
  const { t } = useTranslation();
  const allLabel = t('label_all_units');
  const { myUnits } = useMyUnits();

  const isOrgUnitInUnitTypes = (orgUnit: OrganizationUnit, units: UnitType[]) =>
    !!units ? units.some((r: UnitType) => r.id === orgUnit.fOrgUnitId) : true;

  const hasReviewAccess = myUnits.some((orgUnit) =>
    isOrgUnitInUnitTypes(orgUnit, surveyReviewers),
  );

  const unitList: ChildInput[] = hasReviewAccess
    ? myUnits
        .filter((orgUnit) => isOrgUnitInUnitTypes(orgUnit, surveyAudience))
        .map((unit): ChildInput => {
          return {
            value: unit.fOrgUnitId,
            label: unit.fOrgUnitName,
          };
        })
    : [];

  const dispatch: AppDispatch = useDispatch();
  const [open, setOpen] = useState<boolean>(false);

  const onDialogClose = React.useCallback(() => {
    handleResultsClose();
    setOpen(false);
    (async () => {
      await dispatch(closeAnswerAndResultsDialog());
    })();
  }, [handleResultsClose, dispatch]);

  const print = () => {
    window.print();
  };

  useEffect(() => {
    setOpen(Boolean(resultId));
  }, [resultId]);

  useEffect(() => {
    (async () => {
      if (selectValue === -1) {
        await dispatch(fetchSurveyResults(resultId));
      } else {
        await dispatch(
          fetchFilteredSurveyResults({ id: resultId, unitId: selectValue }),
        );
      }
    })();
  }, [dispatch, resultId, selectValue]);

  useEffect(() => {
    (async () => {
      await dispatch(fetchSurveyAnswers(resultId));
    })();
  }, [dispatch, resultId]);

  const selectOnChange = (value: number) => {
    setSelectValue(value);
  };

  const exportResults = () => {
    exportSurveyResultsToExcel(surveyQuestions, surveyRawResults);
  };

  return {
    surveyResults,
    print,
    open,
    onDialogClose,
    menuList: [{ label: allLabel, value: -1 }, ...unitList],
    selectValue,
    selectOnChange,
    isLoading,
    failedToLoad,
    exportResults,
  };
};
